import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Plus, Trash2 } from 'lucide-react';
import Header from '../../components/Header';
import Tabs from '../../components/Tabs';
import Textarea from '../../components/Textarea';
import TabsCut from '../../components/TabsCut';
import Actions from '../../components/Actions';
import Button from '../../components/Button';
import List from '../../components/List';
import Line from '../../components/Line';
import { UNIQUE_SELLING_POINTS_MAX_COUNT } from '../../utils/const';
import Title from '../../components/Title';
import TabsList from '../../components/TabsList';
import TabsListItem from '../../components/TabsListItem';
import ConfirmationModal from '../../components/ConfirmationModal';
import SvgIcon from '../../components/SvgIcon';
import MobileMenuHeader from '../../components/MobileMenuHeader';

const UniqueValueProposition = ({
  nextClickHandler,
  menuItems,
  selectComponent,
  componentIndex,
  rules,
  backPath,
  locales,
  locales: {
    common,
    tipCustomerSegment,
    caseStudies,
    valueProposition: {
      name,
      uniqueValueProposition: { header, title, text, list, tabs },
    },
  },
}) => {
  const [opened, setOpened] = useState(false);
  const icons = ['roundStar'];
  const iconsComponents = icons.map((e) => <SvgIcon name={e} />);
  const {
    uniqueSellingPoint: {
      addUniqueSellingPoint,
      updateUniqueSellingPoint,
      removeUniqueSellingPoint,
    },
  } = useDispatch();
  const {
    uniqueSellingPoint: { collection },
  } = useSelector((state) => state);

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon={<SvgIcon name="valueProposition" />}
        locales={locales}
        componentIndex={componentIndex}
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        rules={rules}
        backPath={backPath}
        mobileMenuHeader={
          <MobileMenuHeader icon="VPCanvas" title={name} color="green" />
        }
      />

      <div className="bmc-page-text">
        <p className="bmc-page-text--bold">{text[0]}</p>
        <p>{text[1]}</p>
        <p>{text[2]}</p>
      </div>

      <Title text={title[0]} align="left" />

      <List items={list} icons={['roundStar']} />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--green">{title[1]}</span> {title[2]}
      </h2>

      <div className="bmc-page-text">
        <p className="bmc-page-text--center">{text[3]}</p>
      </div>

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-60"
      >
        {[
          <TabsList key="strategy" className="flex flex-col">
            {collection.map((item, index) => (
              <TabsListItem
                className="flex-col md:flex-row"
                divider={
                  index !== collection.length - 1 ||
                  collection.length < UNIQUE_SELLING_POINTS_MAX_COUNT
                }
                key={item.id}
                title={`${tabs.uniqueSellingPoint} ${index + 1}`}
                subtitle={tabs.uniqueSellingPointSub}
                icon={iconsComponents[0]}
                text={
                  // eslint-disable-next-line
                  <Textarea
                    delay={700}
                    value={item.description}
                    onChange={(event) =>
                      updateUniqueSellingPoint({
                        index,
                        name: 'description',
                        value: event.target.value,
                        id: item.id,
                      })
                    }
                    placeholder={tabs.uniqueSellingPointPlaceholder}
                  />
                }
              >
                {collection.length > 1 && (
                  <ConfirmationModal
                    className="ml-3 hover:bg-muted-lighter/60 text-muted h-fit"
                    body={locales.common.deleteConfirmation}
                    confirmText="Delete"
                    isBlockScroll
                    buttonAriaLabel="Delete"
                    onConfirm={() =>
                      removeUniqueSellingPoint({ index, id: item.id })
                    }
                  >
                    <Trash2 className="w-6 min-w-6 h-auto" />
                  </ConfirmationModal>
                )}
              </TabsListItem>
            ))}

            {collection.length < UNIQUE_SELLING_POINTS_MAX_COUNT && (
              <Button
                variant="outline"
                onClick={addUniqueSellingPoint}
                className="mt-10 bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
              >
                <Plus className="w-7 h-auto" />
                {tabs.addNewUniqueValueProposition}
              </Button>
            )}
          </TabsList>,

          <Tabs key="tabs" tabs={caseStudies.map((e) => e.name)} black inner>
            {caseStudies.map((item, index) => (
              <TabsList key={index} preview>
                <TabsCut
                  setOpened={setOpened}
                  opened={opened}
                  caseStudy={item}
                />

                {item.unuqueSellingPoints.map((point, i) => (
                  <TabsListItem
                    divider={false}
                    key={i}
                    title={`${i + 1}. ${tabs.uniqueSellingPoint}`}
                    text={point}
                    icon={iconsComponents[0]}
                  />
                ))}
              </TabsList>
            ))}
          </Tabs>,
        ]}
      </Tabs>

      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
};

export default UniqueValueProposition;
